import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'
import {
  applyPreviewColors,
  applyPreviewLogo,
  loadPreviewColors,
  loadPreviewLogo,
  parsePalette,
} from '@app/application/admin/white-label-preview/utils/preview'
import { useDropdown } from '@app/shared/pinia/dropdown'
import getPiniaInstance from '@app/shared/pinia/instance'

// True from the moment a trigger is clicked until the modal closes, so a
// double click (or a click while the modal is open) cannot mount it twice.
let modalOpen = false

// The modal chunk, shared between the hover warm-up and the click. Reset to
// null on failure so the next attempt imports again instead of reusing a
// rejected promise.
let modalImport = null

function loadModal() {
  if (!modalImport) {
    // Loaded on demand: only an SF admin ever sees the trigger, so the modal
    // itself stays out of the bundle every other user downloads.
    modalImport = import(
      '@app/application/admin/white-label-preview/components/modal.vue'
    )
  }

  return modalImport
}

// Long enough that a chunk already in the browser cache never flashes a
// spinner, short enough that a real network fetch shows one promptly.
const LOADING_OVERLAY_DELAY_MS = 150

function showLoadingOverlay(portal) {
  const overlay = document.createElement('div')
  overlay.className =
    'tw-fixed tw-inset-0 tw-z-dockedModal tw-flex tw-items-center tw-justify-center tw-bg-modal-overlay-docked'
  overlay.innerHTML =
    '<div class="tw-rounded-lg tw-bg-sf-white tw-p-4 tw-shadow-md">' +
    '<i class="fa fa-spinner fa-spin tw-text-2xl tw-text-sf-neutral-dark-2" aria-hidden="true"></i>' +
    '</div>'
  portal.appendChild(overlay)
  return overlay
}

onDomLoad(function ($dom) {
  const triggers = $dom.find("[data-behaviour='white-label-preview-trigger']")
  const trigger = triggers[0]

  if (trigger) {
    const savedColors = loadPreviewColors(trigger.dataset.firmId)
    if (savedColors)
      applyPreviewColors(savedColors, parsePalette(trigger.dataset.palette))

    applyPreviewLogo(loadPreviewLogo(trigger.dataset.firmId))
  }

  triggers.each(function () {
    // Warm the chunk as soon as the pointer reaches the menu entry, so it is
    // usually fetched by the time the click lands.
    this.addEventListener(
      'mouseenter',
      () => {
        loadModal().catch(() => {
          modalImport = null
        })
      },
      { once: true }
    )

    this.addEventListener('click', function (e) {
      e.preventDefault()
      if (modalOpen) return

      useDropdown(getPiniaInstance()).closeDropdown()

      const portal = document.getElementById('sf-modal-portal')
      if (!portal) return

      if (window.modalCitizen) {
        window.modalCitizen.closeAllModals()
      }

      modalOpen = true

      // Read the dataset up front: `currentTarget` is null once the event has
      // finished dispatching, i.e. by the time the import below resolves.
      const { firmId, colors, logoUrl, palette } = e.currentTarget.dataset
      const dbColors = JSON.parse(colors || '{}')
      const sessionColors = loadPreviewColors(firmId)
      const currentColors = sessionColors ?? dbColors

      const originalLogo = logoUrl || ''
      const currentLogo = loadPreviewLogo(firmId)

      let overlay = null
      const overlayTimer = window.setTimeout(() => {
        overlay = showLoadingOverlay(portal)
      }, LOADING_OVERLAY_DELAY_MS)

      loadModal()
        .then(({ default: WhiteLabelPreviewModal }) => {
          const mountTarget = document.createElement('div')
          portal.appendChild(mountTarget)

          const app = createApp(WhiteLabelPreviewModal, {
            firmId,
            palette: parsePalette(palette),
            currentColors,
            originalColors: dbColors,
            currentLogo,
            originalLogo,
            onClose() {
              modalOpen = false
              app.unmount()
              mountTarget.remove()
            },
          })

          app.mount(mountTarget)
        })
        .catch((error) => {
          modalOpen = false
          modalImport = null
          SfLog.error('Could not load the white label preview modal', {
            extra: { error },
          })
        })
        .finally(() => {
          window.clearTimeout(overlayTimer)
          overlay?.remove()
        })
    })
  })
})
